import React, { useState, useEffect } from 'react';
import { Save, X, FileUp, Trash2, ArrowLeft } from 'lucide-react';
import { NEWS_CATEGORIES } from '../../constants/contentCategories';
import PublishingOptionsPanel from '../../components/admin-content-editor/PublishingOptionsPanel';
import { useNewsServices } from '../../apis/newsService';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AddNewsAdminPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    isTrending: false,
    isActive: true,
    commentsEnabled: false
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const { createNews, loading, error } = useNewsServices();

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle content change from Quill editor
  const handleContentChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
    if (errors.content) {
      setErrors(prev => ({ ...prev, content: '' }));
    }
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is an image
      if (!file.type.match('image.*')) {
        toast.error('Only image files are allowed');
        return;
      }
      
      // Check file size (limit to 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image file size should be less than 2MB');
        return;
      }
      
      setImageFile(file);
      
      // Create a preview URL for the image
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Remove selected image
  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const response = await createNews(formData, imageFile);
      
      if (response.success) {
        toast.success('News article created successfully!');
        // Reset form
        setFormData({
          title: '',
          content: '',
          category: '',
          isTrending: false,
          isActive: true,
          commentsEnabled: false
        });
        setImageFile(null);
        setImagePreview('');
      } else {
        toast.error(response.message || 'Failed to create news article');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An error occurred while creating the news article');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show error if API call fails
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image', 'align'
  ];

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-0">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Add New Article</h1>
          <p className="text-gray-500">Create and publish a new news article</p>
        </div>
        <div className="flex w-full sm:w-auto">
          <button 
            type="button"
            className="w-full sm:w-auto justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            onClick={handleSubmit}
            disabled={isSubmitting || loading}
          >
            <Save size={18} className="mr-2" />
            {isSubmitting || loading ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>

      <form className="bg-white shadow-sm rounded-lg overflow-hidden" onSubmit={handleSubmit}>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,17.5rem] xl:grid-cols-[1fr,19rem] gap-6 lg:gap-8 items-start">
            <div className="min-w-0 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Article Title*
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`w-full p-3 border ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent`}
              placeholder="Enter a compelling title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-500">{errors.title}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category*
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className={`w-full p-3 border ${
                errors.category ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent`}
            >
              <option value="">Select a category</option>
              {NEWS_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="mt-1 text-sm text-red-500">{errors.category}</p>
            )}
          </div>
          
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Featured Image
            </label>
            <div className="mt-1 flex items-center">
              {imagePreview ? (
                <div className="flex flex-col items-start space-y-2">
                  <div className="relative">
                    <div className="h-40 bg-gray-200 rounded-md border border-gray-300 flex items-center justify-center">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-full object-contain rounded-md" 
                        style={{
                          width: 'auto',
                          height: 'auto',
                          maxWidth: '100%',
                          maxHeight: '100%'
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex w-full items-center">
                  <label className="flex flex-col items-center px-4 py-2 bg-white text-blue-600 rounded-lg border border-blue-600 cursor-pointer hover:bg-blue-50">
                    <FileUp size={18} className="mb-1" />
                    <span className="text-sm">Upload Image</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>
                  <span className="ml-3 text-sm text-gray-500">Max size: 2MB</span>
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
              Article Content*
            </label>
            <div className={`admin-quill-wrapper border rounded-md overflow-hidden bg-white ${errors.content ? 'border-red-500' : 'border-gray-300'}`}>
              <ReactQuill
                value={formData.content}
                onChange={handleContentChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write your article content here..."
              />
            </div>
            {errors.content && (
              <p className="mt-1 text-sm text-red-500">{errors.content}</p>
            )}
          </div>
            </div>

            <PublishingOptionsPanel>
              <p className="text-xs text-gray-500 -mt-1 mb-1">Visibility and engagement</p>
              <label htmlFor="isActive" className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="mt-0.5 h-4 w-4 shrink-0 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 leading-snug">Active (visible on the site)</span>
              </label>
              <label htmlFor="isTrending" className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="isTrending"
                  name="isTrending"
                  checked={formData.isTrending}
                  onChange={handleInputChange}
                  className="mt-0.5 h-4 w-4 shrink-0 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 leading-snug">Mark as trending news</span>
              </label>
              <label htmlFor="commentsEnabled" className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="commentsEnabled"
                  name="commentsEnabled"
                  checked={formData.commentsEnabled}
                  onChange={handleInputChange}
                  className="mt-0.5 h-4 w-4 shrink-0 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 leading-snug">Enable comments (debate / opinion)</span>
              </label>
            </PublishingOptionsPanel>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddNewsAdminPage;